import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  ChevronRight,
} from "../components/icons";
import { apiClient } from "../lib/api";
import { getMatch as getLoggerMatch } from "../lib/loggerApi";
import { formatPlayerName } from "../lib/nameFormat";
import type {
  Team,
  TeamPlayer,
  PlayerPosition,
  Competition,
  Venue,
  Referee,
  PaginatedResponse,
} from "../types";

interface MatchTeamInfo {
  team_id: string;
  name: string;
  short_name?: string;
  score: number;
  manager: string;
  lineup: LineupPlayerSelection[];
}

interface MatchVenueInfo {
  venue_id: string;
  name: string;
}

interface MatchRefereeInfo {
  referee_id: string;
  name: string;
}

interface MatchRecord {
  _id?: string;
  match_id: string;
  competition_id: string;
  season_name: string;
  competition_stage: string;
  match_date: string;
  tip_off: string;
  venue: MatchVenueInfo;
  referee: MatchRefereeInfo;
  home_team: MatchTeamInfo;
  away_team: MatchTeamInfo;
  status: string;
  logger_status?: string;
  match_time_seconds?: number;
  current_period_start_timestamp?: string | null;
  period_timestamps?: Record<string, { start?: string; end?: string }>;
  actual_start?: string | null;
  actual_end?: string | null;
}

interface MatchCreatePayload {
  match_id: string;
  competition_id: string;
  season_name: string;
  competition_stage: string;
  match_date: string;
  tip_off: string;
  home_team: MatchTeamInfo;
  away_team: MatchTeamInfo;
  venue: MatchVenueInfo;
  referee: MatchRefereeInfo;
}

type MatchUpdatePayload = Partial<
  Pick<
    MatchCreatePayload,
    "match_date" | "tip_off" | "competition_stage" | "season_name"
  >
> & {
  venue?: MatchVenueInfo;
  referee?: MatchRefereeInfo;
};

interface LineupPlayerSelection {
  player_id: string;
  player_name: string;
  position: PlayerPosition;
  jersey_number: number;
  is_starter: boolean;
  is_captain?: boolean;
}

interface MatchFormState {
  match_id: string;
  competition_id: string;
  season_name: string;
  competition_stage: string;
  match_date: string;
  kickoff_time: string;
  venue_id: string;
  referee_id: string;
  home_team_id: string;
  away_team_id: string;
  home_lineup: LineupPlayerSelection[];
  away_lineup: LineupPlayerSelection[];
}

const toArray = <T,>(value: T[] | undefined | null): T[] =>
  Array.isArray(value) ? value : [];

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const formatTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const toIsoDate = (value: string) =>
  new Date(`${value}T00:00:00Z`).toISOString();

const toIsoDateTime = (date: string, time: string) =>
  new Date(`${date}T${time || "00:00"}:00Z`).toISOString();

const createInitialFormState = (): MatchFormState => ({
  match_id: "",
  competition_id: "",
  season_name: "",
  competition_stage: "",
  match_date: "",
  kickoff_time: "",
  venue_id: "",
  referee_id: "",
  home_team_id: "",
  away_team_id: "",
  home_lineup: [],
  away_lineup: [],
});

const MATCH_ID_PATTERN = /^[A-Z0-9_-]{3,64}$/;

const extractTimeFromIso = (value?: string) => {
  if (!value) return "";
  const match = value.match(/T(\d{2}:\d{2})/);
  return match?.[1] ?? "";
};

const generateMatchId = (data: {
  competition_id?: string;
  home_team_id?: string;
  away_team_id?: string;
  match_date?: string;
}) => {
  const comp = (data.competition_id || "MATCH")
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");
  const home = (data.home_team_id || "HOME")
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");
  const away = (data.away_team_id || "AWAY")
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");
  const datePart = data.match_date ? data.match_date.replace(/-/g, "") : "TBD";
  const suffix = Math.random().toString(16).slice(2, 8).toUpperCase();
  return `${comp}_${home}_${away}_${datePart}_${suffix}`.slice(0, 64);
};

export default function MatchesManager() {
  const { t } = useTranslation("admin");
  const [matches, setMatches] = useState<MatchRecord[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [referees, setReferees] = useState<Referee[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [editingItem, setEditingItem] = useState<MatchRecord | null>(null);

  // Wizard form data
  const [matchData, setMatchData] = useState<MatchFormState>(
    createInitialFormState(),
  );

  // Team rosters for lineup builder
  const [homeRoster, setHomeRoster] = useState<TeamPlayer[]>([]);
  const [awayRoster, setAwayRoster] = useState<TeamPlayer[]>([]);

  useEffect(() => {
    fetchMatches();
    fetchCompetitions();
    fetchVenues();
    fetchReferees();
    fetchTeams();
  }, []);

  // Auto-generate match_id for new matches when core fields change
  useEffect(() => {
    if (editingItem) return; // keep existing ID for edits
    const nextId = generateMatchId({
      competition_id: matchData.competition_id,
      home_team_id: matchData.home_team_id,
      away_team_id: matchData.away_team_id,
      match_date: matchData.match_date,
    });
    setMatchData((prev) => ({ ...prev, match_id: nextId }));
  }, [
    editingItem,
    matchData.competition_id,
    matchData.home_team_id,
    matchData.away_team_id,
    matchData.match_date,
  ]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      setPageError(null);
      const data = await apiClient.get<MatchRecord[]>("/matches/");
      setMatches(data);

      // Refresh statuses from logger service to reflect live state.
      // We attempt by match_id first, then fall back to _id (some environments differ).
      const fetchLiveStatus = async (item: MatchRecord) => {
        try {
          const live = await getLoggerMatch(item.match_id);
          return live.status as string | undefined;
        } catch (err) {
          // Try by _id if provided
          if (item._id) {
            try {
              const live = await getLoggerMatch(item._id);
              return live.status as string | undefined;
            } catch (err2) {
              console.error(
                "Status refresh failed for match",
                item.match_id,
                "(_id fallback tried)",
                err2,
              );
            }
          } else {
            console.error(
              "Status refresh failed for match",
              item.match_id,
              err,
            );
          }
        }
        return undefined;
      };

      try {
        const enriched = await Promise.all(
          data.map(async (item) => {
            const liveStatus = await fetchLiveStatus(item);
            return {
              ...item,
              logger_status: liveStatus,
              status: liveStatus ?? item.status,
            } as MatchRecord;
          }),
        );
        setMatches(enriched);
      } catch (refreshErr) {
        console.error("Failed to refresh live statuses", refreshErr);
      }
    } catch (err: any) {
      setPageError(err.response?.data?.detail || t("errorFetchingData"));
    } finally {
      setLoading(false);
    }
    setHasInitialLoadCompleted(true);
  };

  const fetchCompetitions = async () => {
    try {
      const response = await apiClient.get<any>("/competitions/");
      setCompetitions(
        Array.isArray(response) ? response : response.items || [],
      );
    } catch (err) {
      console.error("Error fetching competitions:", err);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await apiClient.get<any>("/venues/");
      setVenues(Array.isArray(response) ? response : response.items || []);
    } catch (err) {
      console.error("Error fetching venues:", err);
    }
  };

  const fetchReferees = async () => {
    try {
      const response = await apiClient.get<any>("/referees/");
      setReferees(Array.isArray(response) ? response : response.items || []);
    } catch (err) {
      console.error("Error fetching referees:", err);
    }
  };

  const fetchTeams = async () => {
    try {
      let page = 1;
      const pageSize = 100; // backend max is 100
      const allTeams: Team[] = [];
      let hasMore = true;
      while (hasMore) {
        const data = await apiClient.get<PaginatedResponse<Team> | Team[]>(
          "/teams/",
          { params: { page, page_size: pageSize } },
        );
        if ("items" in data && Array.isArray(data.items)) {
          allTeams.push(...data.items);
          const total =
            (data as PaginatedResponse<Team>).total ?? data.items.length;
          hasMore = allTeams.length < total;
        } else if (Array.isArray(data)) {
          allTeams.push(...data);
          hasMore = false;
        } else {
          hasMore = false;
        }
        page += 1;
      }
      setTeams(allTeams);
    } catch (err) {
      console.error("Error fetching teams:", err);
    }
  };

  const fetchTeamRoster = async (teamId: string): Promise<TeamPlayer[]> => {
    try {
      const normalizedTeamId = (teamId || "").trim();
      let page = 1;
      const pageSize = 100; // backend max is 100
      const roster: TeamPlayer[] = [];
      let hasMore = true;
      while (hasMore) {
        const resp = await apiClient.get<
          PaginatedResponse<TeamPlayer> | TeamPlayer[]
        >(`/teams/${normalizedTeamId}/players`, {
          params: { page, page_size: pageSize },
        });
        if ("items" in resp && Array.isArray(resp.items)) {
          roster.push(
            ...resp.items.map((player) => ({
              ...player,
              player_name: formatPlayerName(player.player_name),
            })),
          );
          const total = resp.total ?? resp.items.length;
          hasMore = roster.length < total;
        } else if (Array.isArray(resp)) {
          roster.push(
            ...resp.map((player) => ({
              ...player,
              player_name: formatPlayerName(player.player_name),
            })),
          );
          hasMore = false;
        } else {
          hasMore = false;
        }
        page += 1;
      }
      return roster;
    } catch (err) {
      console.error("Error fetching team roster:", err);
      // Surface roster fetch failures in the modal so users know why they can't proceed.
      setWizardError(
        (err as any)?.response?.data?.detail ||
          (err as any)?.message ||
          "Unable to load team roster. Please try again.",
      );
      return [];
    }
  };

  const validateBasicInfo = (): string | null => {
    const requiredFields: Array<[string, string]> = [
      [matchData.match_id, "Match ID"],
      [matchData.competition_id, "Competition"],
      [matchData.season_name, "Season"],
      [matchData.competition_stage, "Competition stage"],
      [matchData.home_team_id, "Home team"],
      [matchData.away_team_id, "Away team"],
      [matchData.match_date, "Match date"],
      [matchData.kickoff_time, "Kickoff time"],
      [matchData.venue_id, "Venue"],
      [matchData.referee_id, "Referee"],
    ];

    const missing = requiredFields.find(
      ([value]) => !value || value.trim() === "",
    );
    if (missing) {
      return `${missing[1]} is required`;
    }

    if (matchData.home_team_id === matchData.away_team_id) {
      return "Home and away teams must be different";
    }

    if (matchData.season_name.trim().length < 4) {
      return "Season must be at least 4 characters";
    }

    if (!MATCH_ID_PATTERN.test(matchData.match_id)) {
      return "Match ID must be 3-64 characters (A-Z, 0-9, underscore, hyphen)";
    }

    return null;
  };

  const validateMatchForm = (includeLineups = true): string | null => {
    const basicError = validateBasicInfo();
    if (basicError) {
      return basicError;
    }

    if (!includeLineups) {
      return null;
    }

    const homeStarters = matchData.home_lineup.filter((p) => p.is_starter);
    const awayStarters = matchData.away_lineup.filter((p) => p.is_starter);

    if (homeStarters.length !== 5) {
      return "Home lineup must include exactly 5 starters";
    }
    if (awayStarters.length !== 5) {
      return "Away lineup must include exactly 5 starters";
    }
    if (
      matchData.home_lineup.length < 5 ||
      matchData.away_lineup.length < 5
    ) {
      return "Each team must register at least 5 players";
    }

    return null;
  };

  const resolveTeamInfo = (
    teamId: string,
    lineup: LineupPlayerSelection[],
  ): MatchTeamInfo => {
    const team = toArray(teams).find((t) => t.team_id === teamId);
    if (!team) {
      throw new Error(`Team ${teamId} not found`);
    }
    const managerName =
      team.managers?.[0]?.name || team.manager?.name || team.name;
    return {
      team_id: team.team_id || teamId,
      name: team.name,
      short_name: team.short_name,
      score: 0,
      manager: managerName || team.name,
      lineup: lineup.map((player) => ({
        ...player,
        is_captain: player.is_captain ?? false,
      })),
    };
  };

  const resolveVenueInfo = (venueId: string): MatchVenueInfo => {
    const venue = toArray(venues).find((v) => v.venue_id === venueId);
    if (!venue) {
      throw new Error(`Venue ${venueId} not found`);
    }
    return {
      venue_id: venue.venue_id || venueId,
      name: venue.name,
    };
  };

  const resolveRefereeInfo = (refereeId: string): MatchRefereeInfo => {
    const referee = toArray(referees).find((r) => r.referee_id === refereeId);
    if (!referee) {
      throw new Error(`Referee ${refereeId} not found`);
    }
    return {
      referee_id: referee.referee_id || refereeId,
      name: referee.name,
    };
  };

  const buildMatchCreatePayload = (): MatchCreatePayload => {
    const homeTeam = resolveTeamInfo(
      matchData.home_team_id,
      matchData.home_lineup,
    );
    const awayTeam = resolveTeamInfo(
      matchData.away_team_id,
      matchData.away_lineup,
    );
    const venue = resolveVenueInfo(matchData.venue_id);
    const referee = resolveRefereeInfo(matchData.referee_id);

    return {
      match_id: matchData.match_id.trim(),
      competition_id: matchData.competition_id,
      season_name: matchData.season_name.trim(),
      competition_stage: matchData.competition_stage.trim(),
      match_date: toIsoDate(matchData.match_date),
      tip_off: toIsoDateTime(matchData.match_date, matchData.kickoff_time),
      home_team: homeTeam,
      away_team: awayTeam,
      venue,
      referee,
    };
  };

  const handleTeamSelection = async () => {
    setRosterLoading(true);
    if (matchData.home_team_id) {
      const roster = await fetchTeamRoster(matchData.home_team_id);
      setHomeRoster(roster);
      if (matchData.home_lineup.length === 0) {
        const starters = roster.filter((p) => p.is_starter).slice(0, 5);
        setMatchData((prev) => ({
          ...prev,
          home_lineup: starters.map((p) => ({
            player_id: p.player_id,
            player_name: formatPlayerName(p.player_name || p.player_id),
            position: p.position,
            jersey_number: p.jersey_number,
            is_starter: true,
          })),
        }));
      }
    }
    if (matchData.away_team_id) {
      const roster = await fetchTeamRoster(matchData.away_team_id);
      setAwayRoster(roster);
      if (matchData.away_lineup.length === 0) {
        const starters = roster.filter((p) => p.is_starter).slice(0, 5);
        setMatchData((prev) => ({
          ...prev,
          away_lineup: starters.map((p) => ({
            player_id: p.player_id,
            player_name: formatPlayerName(p.player_name || p.player_id),
            position: p.position,
            jersey_number: p.jersey_number,
            is_starter: true,
          })),
        }));
      }
    }
    setRosterLoading(false);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setWizardError(null);
    const validationError = validateMatchForm(true);
    if (validationError) {
      setWizardError(validationError);
      setSaving(false);
      return;
    }

    try {
      if (editingItem) {
        const identifier = editingItem._id || editingItem.match_id;
        if (!identifier) {
          throw new Error("Unable to edit match: missing identifier");
        }
        const updatePayload: MatchUpdatePayload = {
          match_date: toIsoDate(matchData.match_date),
          tip_off: toIsoDateTime(matchData.match_date, matchData.kickoff_time),
          competition_stage: matchData.competition_stage.trim(),
          season_name: matchData.season_name.trim(),
          venue: resolveVenueInfo(matchData.venue_id),
          referee: resolveRefereeInfo(matchData.referee_id),
        };
        await apiClient.put(`/matches/${identifier}`, updatePayload);
        await apiClient.patch(`/matches/${identifier}/lineups`, {
          home_team_lineup: matchData.home_lineup,
          away_team_lineup: matchData.away_lineup,
        });
      } else {
        const payload = buildMatchCreatePayload();
        await apiClient.post("/matches/", payload);
      }
      await fetchMatches();
      handleCloseWizard();
    } catch (err: any) {
      if (err?.message && !err?.response) {
        setWizardError(err.message);
        setSaving(false);
        return;
      }
      setWizardError(err?.response?.data?.detail || t("errorSavingData"));
      setSaving(false);
    }
  };

  const handleDelete = async (match: MatchRecord) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      const identifier = match._id || match.match_id;
      await apiClient.delete(`/matches/${identifier}`);
      await fetchMatches();
    } catch (err: any) {
      setPageError(err.response?.data?.detail || t("errorDeletingData"));
    }
  };

  const handleEdit = async (item: MatchRecord) => {
    setWizardLoading(true);
    setEditingItem(item);
    const kickoffTime = extractTimeFromIso(item.tip_off);

    setMatchData({
      match_id: item.match_id,
      competition_id: item.competition_id ? String(item.competition_id) : "",
      season_name: item.season_name,
      competition_stage: item.competition_stage,
      match_date: item.match_date ? item.match_date.slice(0, 10) : "",
      kickoff_time: kickoffTime,
      venue_id: item.venue?.venue_id ? String(item.venue.venue_id) : "",
      referee_id: item.referee?.referee_id
        ? String(item.referee.referee_id)
        : "",
      home_team_id: item.home_team?.team_id
        ? String(item.home_team.team_id)
        : "",
      away_team_id: item.away_team?.team_id
        ? String(item.away_team.team_id)
        : "",
      home_lineup: normalizeLineup(item.home_team?.lineup),
      away_lineup: normalizeLineup(item.away_team?.lineup),
    });

    setWizardStep(1);
    setShowWizard(true);

    if (item.home_team?.team_id) {
      setHomeRoster(await fetchTeamRoster(item.home_team.team_id));
    }
    if (item.away_team?.team_id) {
      setAwayRoster(await fetchTeamRoster(item.away_team.team_id));
    }
    setWizardLoading(false);
  };

  const handleCloseWizard = () => {
    setShowWizard(false);
    setWizardStep(1);
    setEditingItem(null);
    setMatchData(createInitialFormState());
    setHomeRoster([]);
    setAwayRoster([]);
    setWizardError(null);
    setWizardLoading(false);
    setRosterLoading(false);
    setSaving(false);
  };

  const handleNextStep = async () => {
    setWizardError(null);
    if (wizardStep === 1) {
      const validation = validateBasicInfo();
      if (validation) {
        setWizardError(validation);
        return;
      }
      await handleTeamSelection();
    }
    setWizardStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    setWizardStep(wizardStep - 1);
  };

  const toggleLineupPlayer = (
    teamType: "home" | "away",
    player: TeamPlayer,
    isStarter: boolean,
    shouldSelect: boolean,
  ) => {
    setWizardError(null);
    if (
      !player.position ||
      player.jersey_number === undefined ||
      player.jersey_number === null
    ) {
      setWizardError("Player must have a position and jersey number");
      return;
    }

    const lineupKey = teamType === "home" ? "home_lineup" : "away_lineup";
    const currentLineup = matchData[lineupKey] || [];

    const existingIndex = currentLineup.findIndex(
      (p) => p.player_id === player.player_id,
    );
    const startersCount = currentLineup.filter((p) => p.is_starter).length;

    if (!shouldSelect) {
      if (existingIndex >= 0) {
        const newLineup = currentLineup.filter(
          (p) => p.player_id !== player.player_id,
        );
        setMatchData({ ...matchData, [lineupKey]: newLineup });
      }
      return;
    }

    if (existingIndex >= 0) {
      const updated = [...currentLineup];
      updated[existingIndex] = {
        ...updated[existingIndex],
        is_starter: isStarter,
      };
      const nextStarters = updated.filter((p) => p.is_starter).length;
      if (nextStarters > 5) {
        setWizardError("Cannot select more than 5 starters");
        return;
      }
      setMatchData({ ...matchData, [lineupKey]: updated });
    } else {
      if (isStarter && startersCount >= 5) {
        setWizardError("Cannot select more than 5 starters");
        return;
      }
      const newPlayer: LineupPlayerSelection = {
        player_id: player.player_id,
        player_name: formatPlayerName(player.player_name || player.player_id),
        position: player.position,
        jersey_number: player.jersey_number,
        is_starter: isStarter,
      };
      setMatchData({
        ...matchData,
        [lineupKey]: [...currentLineup, newPlayer],
      });
    }
  };

  const isPlayerInLineup = (
    teamType: "home" | "away",
    playerId: string,
  ): boolean => {
    const lineupKey = teamType === "home" ? "home_lineup" : "away_lineup";
    const lineup = matchData[lineupKey] || [];
    return lineup.some((p) => p.player_id === playerId);
  };

  const getLineupCount = (
    teamType: "home" | "away",
    starterOnly: boolean = false,
  ): number => {
    const lineupKey = teamType === "home" ? "home_lineup" : "away_lineup";
    const lineup = matchData[lineupKey] || [];
    return starterOnly
      ? lineup.filter((p) => p.is_starter).length
      : lineup.length;
  };

  const getTeamName = (teamId?: string): string => {
    if (!teamId) {
      return "";
    }
    const team = toArray(teams).find((t) => t.team_id === teamId);
    return team?.name || teamId || "";
  };

  const getPositionBadgeColor = (position: PlayerPosition) => {
    switch (position) {
      case "PG":
      case "SG":
        return "bg-blue-100 text-blue-800";
      case "SF":
      case "PF":
        return "bg-green-100 text-green-800";
      case "C":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const normalizeLineup = (
    lineup?: LineupPlayerSelection[] | any[],
  ): LineupPlayerSelection[] =>
    toArray(lineup).map((player) => ({
      player_id: player.player_id,
      player_name: formatPlayerName(player.player_name || player.player_id),
      position: player.position,
      jersey_number: player.jersey_number,
      is_starter: player.is_starter ?? true,
      is_captain: player.is_captain ?? false,
    }));

  const sortRosterByJerseyNumber = (roster: TeamPlayer[]): TeamPlayer[] =>
    [...roster].sort((left, right) => {
      const leftJersey = Number(left.jersey_number);
      const rightJersey = Number(right.jersey_number);
      const leftSortable = Number.isFinite(leftJersey)
        ? leftJersey
        : Number.MAX_SAFE_INTEGER;
      const rightSortable = Number.isFinite(rightJersey)
        ? rightJersey
        : Number.MAX_SAFE_INTEGER;
      if (leftSortable !== rightSortable) {
        return leftSortable - rightSortable;
      }
      return String(left.player_name || left.player_id).localeCompare(
        String(right.player_name || right.player_id),
      );
    });

  const safeCompetitions = toArray(competitions);
  const safeTeams = toArray(teams);
  const safeVenues = toArray(venues);
  const safeReferees = toArray(referees);
  const safeMatches = toArray(matches);
  const filterActivePlayers = (roster: TeamPlayer[]) =>
    roster.filter((p) => (p as any).is_active !== false);
  const sortedHomeRosterCandidates = sortRosterByJerseyNumber(
    filterActivePlayers(homeRoster),
  );
  const sortedAwayRosterCandidates = sortRosterByJerseyNumber(
    filterActivePlayers(awayRoster),
  );
  const totalSteps = 3;
  const showLineupSteps = true;
  const isLastStep = wizardStep === 3;

  const lowerSearch = searchTerm.toLowerCase();
  const filteredMatches = safeMatches.filter((item) => {
    const homeName = item.home_team?.name?.toLowerCase() || "";
    const awayName = item.away_team?.name?.toLowerCase() || "";
    const matchId = item.match_id?.toLowerCase() || "";
    const matchDate = formatDate(item.match_date).toLowerCase();
    return (
      homeName.includes(lowerSearch) ||
      awayName.includes(lowerSearch) ||
      matchId.includes(lowerSearch) ||
      matchDate.includes(lowerSearch)
    );
  });

  const deriveDisplayStatus = (item: MatchRecord): string | undefined => {
    const status = item.logger_status ?? item.status;
    // Mirror logger fallback: if no time recorded and no period has started, treat as Pending
    const noClockProgress = (item.match_time_seconds ?? 0) <= 0;
    const noPeriodStart = !item.current_period_start_timestamp;
    const noFirstHalfStart = !item.period_timestamps?.["1"]?.start;
    if (
      status === "Fulltime" &&
      noClockProgress &&
      noPeriodStart &&
      noFirstHalfStart
    ) {
      return "Pending";
    }
    return status;
  };

  if (loading && !hasInitialLoadCompleted) {
    return (
      <div className="flex items-center justify-center h-64">
        {t("loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Calendar className="h-8 w-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t("matches")}</h1>
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setMatchData(createInitialFormState());
            setWizardStep(1);
            setShowWizard(true);
          }}
          className="btn btn-primary flex items-center space-x-2"
          data-testid="create-match-btn"
        >
          <Plus className="h-5 w-5" />
          <span>{t("createMatch")}</span>
        </button>
      </div>

      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {pageError}
        </div>
      )}

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t("search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>

      <div className="card">
        {filteredMatches.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? t("noData") : t("noMatches")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t("matchDate")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t("homeTeam")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t("awayTeam")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t("venue")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Logger Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMatches.map((item) => {
                  const displayStatus = deriveDisplayStatus(item);
                  const loggerStatus = item.logger_status
                    ? deriveDisplayStatus(item)
                    : undefined;
                  return (
                    <tr
                      key={item._id || item.match_id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(item.match_date)}{" "}
                        {formatTime(item.tip_off)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.home_team?.name || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.away_team?.name || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.venue?.name || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            displayStatus === "Fulltime"
                              ? "bg-green-100 text-green-800"
                              : displayStatus?.toLowerCase().includes("live")
                                ? "bg-yellow-100 text-yellow-800"
                                : displayStatus === "Pending"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {displayStatus?.replace(/_/g, " ") || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {loggerStatus ? (
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              loggerStatus === "Fulltime"
                                ? "bg-green-100 text-green-800"
                                : loggerStatus?.toLowerCase().includes("live")
                                  ? "bg-yellow-100 text-yellow-800"
                                  : loggerStatus === "Pending"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {loggerStatus.replace(/_/g, " ")}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() =>
                            (window.location.href = `/matches/${item.match_id}/logger`)
                          }
                          className="text-green-600 hover:text-green-900"
                          title={t("startLogger")}
                        >
                          <Calendar className="h-5 w-5 inline" />
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t("edit")}
                        >
                          <Edit className="h-5 w-5 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="text-red-600 hover:text-red-900"
                          title={t("delete")}
                        >
                          <Trash2 className="h-5 w-5 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Match Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
            {(wizardLoading || rosterLoading || saving) && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-20">
                <div className="flex items-center space-x-3 text-gray-700">
                  <div
                    className="h-6 w-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
                    aria-label="loading-spinner"
                  />
                  <span>
                    {saving
                      ? "Saving match..."
                      : wizardLoading
                        ? "Loading lineups..."
                        : "Loading players..."}
                  </span>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold" data-testid="modal-title">
                  {editingItem ? t("edit") : t("createMatch")}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t("competitionStage")} {wizardStep} / {totalSteps}
                </p>
              </div>
              <button
                onClick={handleCloseWizard}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {wizardError && (
                <div
                  className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg"
                  role="alert"
                >
                  {wizardError}
                </div>
              )}

              {/* Step Indicator */}
              <div className="flex items-center justify-center mb-8">
                {showLineupSteps ? (
                  <div className="flex items-center space-x-4">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full ${
                        wizardStep >= 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      1
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full ${
                        wizardStep >= 2
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      2
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full ${
                        wizardStep >= 3
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      3
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white">
                    1
                  </div>
                )}
              </div>

              {/* Step 1: Basic Info */}
              {wizardStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">{t("details")}</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("id")} *
                    </label>
                    <input
                      type="text"
                      required
                      readOnly
                      disabled
                      value={matchData.match_id}
                      onChange={(e) =>
                        setMatchData({ ...matchData, match_id: e.target.value })
                      }
                      className="input w-full bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("competitions")} *
                    </label>
                    <select
                      required
                      value={matchData.competition_id}
                      onChange={(e) =>
                        setMatchData({
                          ...matchData,
                          competition_id: e.target.value,
                        })
                      }
                      className="input w-full"
                      data-testid="competitions-select"
                    >
                      <option value="">Select...</option>
                      {safeCompetitions.map((comp) => (
                        <option
                          key={comp.competition_id}
                          value={String(comp.competition_id)}
                        >
                          {comp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("season", { defaultValue: "Season" })} *
                      </label>
                      <input
                        type="text"
                        required
                        value={matchData.season_name}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            season_name: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="season-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("competitionStage")} *
                      </label>
                      <input
                        type="text"
                        required
                        value={matchData.competition_stage}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            competition_stage: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="competition-stage-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("homeTeam")} *
                      </label>
                      <select
                        required
                        value={matchData.home_team_id}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            home_team_id: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="home-team-select"
                      >
                        <option value="">{t("selectTeam")}</option>
                        {safeTeams.map((team) => (
                          <option
                            key={team.team_id}
                            value={String(team.team_id)}
                          >
                            {team.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("awayTeam")} *
                      </label>
                      <select
                        required
                        value={matchData.away_team_id}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            away_team_id: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="away-team-select"
                      >
                        <option value="">{t("selectTeam")}</option>
                        {safeTeams.map((team) => (
                          <option
                            key={team.team_id}
                            value={String(team.team_id)}
                          >
                            {team.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("matchDate")} *
                      </label>
                      <input
                        type="date"
                        required
                        value={matchData.match_date}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            match_date: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="match-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("kickoffTime")} *
                      </label>
                      <input
                        type="time"
                        required
                        value={matchData.kickoff_time}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            kickoff_time: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="kickoff-time-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("venue")} *
                      </label>
                      <select
                        required
                        value={matchData.venue_id}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            venue_id: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="venue-select"
                      >
                        <option value="">Select...</option>
                        {safeVenues.map((venue) => (
                          <option
                            key={venue.venue_id}
                            value={String(venue.venue_id)}
                          >
                            {venue.name} - {venue.city}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("referee")} *
                      </label>
                      <select
                        required
                        value={matchData.referee_id}
                        onChange={(e) =>
                          setMatchData({
                            ...matchData,
                            referee_id: e.target.value,
                          })
                        }
                        className="input w-full"
                        data-testid="referee-select"
                      >
                        <option value="">Select...</option>
                        {safeReferees.map((ref) => (
                          <option
                            key={ref.referee_id}
                            value={String(ref.referee_id)}
                          >
                            {ref.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Home Team Lineup */}
              {showLineupSteps && wizardStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    {t("lineup")} - {getTeamName(matchData.home_team_id)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t("starters")}: {getLineupCount("home", true)} |{" "}
                    {t("substitutes")}:{" "}
                    {getLineupCount("home", false) -
                      getLineupCount("home", true)}
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3">{t("starters")}</h4>
                      <div
                        className="space-y-2"
                        data-testid="home-starters-candidates"
                      >
                        {sortedHomeRosterCandidates.map((player) => {
                          const selectable =
                            player.position &&
                            player.jersey_number !== undefined &&
                            player.jersey_number !== null;
                          return (
                            <div
                              key={player.player_id}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={
                                    isPlayerInLineup(
                                      "home",
                                      player.player_id,
                                    ) &&
                                    matchData.home_lineup.some(
                                      (p) =>
                                        p.player_id === player.player_id &&
                                        p.is_starter,
                                    )
                                  }
                                  onChange={(e) =>
                                    toggleLineupPlayer(
                                      "home",
                                      player,
                                      true,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={!selectable}
                                  className="h-4 w-4"
                                />
                                <span className="font-semibold">
                                  #{player.jersey_number}
                                </span>
                                <span className="text-sm text-gray-800">
                                  {player.player_name || player.player_id}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
                                    player.position,
                                  )}`}
                                >
                                  {t(`positions.${player.position}`)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">{t("substitutes")}</h4>
                      <div
                        className="space-y-2"
                        data-testid="home-substitutes-candidates"
                      >
                        {sortedHomeRosterCandidates.map((player) => {
                          const selectable =
                            player.position &&
                            player.jersey_number !== undefined &&
                            player.jersey_number !== null;
                          return (
                            <div
                              key={player.player_id}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={
                                    isPlayerInLineup(
                                      "home",
                                      player.player_id,
                                    ) &&
                                    matchData.home_lineup.some(
                                      (p) =>
                                        p.player_id === player.player_id &&
                                        !p.is_starter,
                                    )
                                  }
                                  onChange={(e) =>
                                    toggleLineupPlayer(
                                      "home",
                                      player,
                                      false,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={!selectable}
                                  className="h-4 w-4"
                                />
                                <span className="font-semibold">
                                  #{player.jersey_number}
                                </span>
                                <span className="text-sm text-gray-800">
                                  {player.player_name || player.player_id}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
                                    player.position,
                                  )}`}
                                >
                                  {t(`positions.${player.position}`)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Away Team Lineup */}
              {showLineupSteps && wizardStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    {t("lineup")} - {getTeamName(matchData.away_team_id)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t("starters")}: {getLineupCount("away", true)} |{" "}
                    {t("substitutes")}:{" "}
                    {getLineupCount("away", false) -
                      getLineupCount("away", true)}
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3">{t("starters")}</h4>
                      <div
                        className="space-y-2"
                        data-testid="away-starters-candidates"
                      >
                        {sortedAwayRosterCandidates.map((player) => {
                          const selectable =
                            player.position &&
                            player.jersey_number !== undefined &&
                            player.jersey_number !== null;
                          return (
                            <div
                              key={player.player_id}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={
                                    isPlayerInLineup(
                                      "away",
                                      player.player_id,
                                    ) &&
                                    matchData.away_lineup.some(
                                      (p) =>
                                        p.player_id === player.player_id &&
                                        p.is_starter,
                                    )
                                  }
                                  onChange={(e) =>
                                    toggleLineupPlayer(
                                      "away",
                                      player,
                                      true,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={!selectable}
                                  className="h-4 w-4"
                                />
                                <span className="font-semibold">
                                  #{player.jersey_number}
                                </span>
                                <span className="text-sm text-gray-800">
                                  {player.player_name || player.player_id}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
                                    player.position,
                                  )}`}
                                >
                                  {t(`positions.${player.position}`)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">{t("substitutes")}</h4>
                      <div
                        className="space-y-2"
                        data-testid="away-substitutes-candidates"
                      >
                        {sortedAwayRosterCandidates.map((player) => {
                          const selectable =
                            player.position &&
                            player.jersey_number !== undefined &&
                            player.jersey_number !== null;
                          return (
                            <div
                              key={player.player_id}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={
                                    isPlayerInLineup(
                                      "away",
                                      player.player_id,
                                    ) &&
                                    matchData.away_lineup.some(
                                      (p) =>
                                        p.player_id === player.player_id &&
                                        !p.is_starter,
                                    )
                                  }
                                  onChange={(e) =>
                                    toggleLineupPlayer(
                                      "away",
                                      player,
                                      false,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={!selectable}
                                  className="h-4 w-4"
                                />
                                <span className="font-semibold">
                                  #{player.jersey_number}
                                </span>
                                <span className="text-sm text-gray-800">
                                  {player.player_name || player.player_id}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
                                    player.position,
                                  )}`}
                                >
                                  {t(`positions.${player.position}`)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6 border-t mt-6">
                <button
                  type="button"
                  onClick={
                    wizardStep > 1 ? handlePreviousStep : handleCloseWizard
                  }
                  className="btn btn-secondary"
                  disabled={saving || wizardLoading || rosterLoading}
                >
                  {wizardStep > 1 ? t("previous") : t("cancel")}
                </button>
                {!isLastStep ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="btn btn-primary"
                    disabled={saving || wizardLoading || rosterLoading}
                  >
                    {t("next")} <ChevronRight className="h-5 w-5 inline ml-1" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-primary flex items-center"
                    disabled={saving || wizardLoading || rosterLoading}
                  >
                    {saving && (
                      <span
                        className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
                        aria-label="saving-spinner"
                      />
                    )}
                    {t("save")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
